/* eslint-disable multiline-ternary */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import classNames from 'classnames';
import EmojiPicker from 'emoji-picker-react';
import { v4 as uuidv4 } from 'uuid';
import { Loader, Modal, Typography } from 'src/components/common';
import ProfilePicture from 'src/components/profile/profilePicture/ProfilePicture';
import TextAreaField from 'src/components/common/formFields/textAreaField/TextAreaField';
import Button from 'src/components/button/Button';
import { PictureIcon, VideoIcon, EmojiIcon, TrashIcon } from 'src/components/common/common.icons';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { StorageUtils, textFormatter } from 'src/utils';
import debounce from 'lodash.debounce';
import {
  TCreatePostRequest,
  TDeleteMediaActionRequest,
  TEditPostRequest,
  TGetPostPresignedUrlActionRequest
} from 'src/services/apiEndpoint.types.ts';
import {
  TCreatePostMedia,
  clearPostMedia,
  createPost,
  deleteMedia,
  editPost,
  getPostPresignedUrl,
  startMediaLoader,
  topFeedItems
} from 'src/store/socialMedia/socialMedia.slice';
import { useUploadMedia } from 'src/hooks';
import Label from 'src/components/common/formFields/label/Label';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { TPostType, componentSizeVariantsEnum } from 'src/constants';
import { TUserTypes } from 'src/constants/user.constants';
import { TMediaType } from 'src/hooks/useUploadMedia/useUploadMedia.types';

import './createEditPostModal.scss';
import createEditPostModalValidationSchema, {
  createEditAnnouncementModalValidationSchema
} from './createEditPostModal.validationSchema';
import axios from 'axios';
import LinkCard from 'src/components/cards/LinkCard/LinkCard';
import { Col, Row } from 'react-bootstrap';
import PollIcon from 'src/components/common/common.icons/PollIcon';
import { MdAdd } from 'react-icons/md';
import { Prev } from 'react-bootstrap/esm/PageItem';
import { options } from 'linkifyjs';
import { error, log } from 'console';
import { TInitialPoll } from 'src/services/apiEndpoint.types.ts/socialMedia/createPost.endpoint.types';
import { Poll } from '../postCard/PostCard';
import { getMetaData } from 'src/store/chat/chat.slice';

export type TCreateEditPostModal = {
  type?: TPostType;
  isPoll?: boolean;
  pollData?: Poll | null;
  companyProfile: boolean;
  title: string;
  isModalOpen: boolean;
  onModalClose: () => void;
  postDescription?: string;
  editMode?: boolean;
  postText?: string;
  postTitle?: string;
  postCustomButton?: string;
  postRedirectUrl?: string;
  editPostId?: string;
  refetchPost?: () => void;
  viewerId: string;
  viewerType: TUserTypes;
  shareProgramMode?: boolean;
  setEmoji?: React.Dispatch<React.SetStateAction<string>>;
  emoji?: string;
  linkPreview?: boolean
};

export type TCreateEditPostModalFormValues = {
  text: string;
  title: string;
  customButton: string;
  redirectUrl: string;
};

const CreateEditPostModal = ({
  type = 'post',
  isPoll: isPollValue = false,
  pollData = null,
  companyProfile = false,
  title,
  isModalOpen,
  onModalClose,
  postText = '',
  postTitle = '',
  postCustomButton = '',
  postRedirectUrl = '',
  editMode,
  editPostId = '',
  refetchPost,
  shareProgramMode = false,
  setEmoji = () => { },
  emoji = '',
  linkPreview = false
}: TCreateEditPostModal) => {
  const initialValues: TCreateEditPostModalFormValues = {
    text: postText || '',
    title: postTitle || '',
    customButton: postCustomButton || '',
    redirectUrl: postRedirectUrl || ''
  };
  const initialPoll = {
    question: '',
    allowMultipleValues: false,
    // expiryDate: '',
    // expiryTime: '',
    options: [
      '',
      ''
    ]
  }
  console.log(postText, 'postTextData');

  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const [isPoll, setIsPole] = useState<boolean>(isPollValue)
  const [poll, setPoll] = useState<TInitialPoll>(initialPoll);
  const [errorTrig, setErrorTrig] = useState<boolean>(false)
  const [postType, setPostType] = useState<TPostType>(type);
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const uploadVideoRef = useRef<HTMLInputElement>(null);
  const postDescriptionRef = useRef<HTMLTextAreaElement>(null);

  const { profile } = useAppSelector((store) => store.profile);
  const { myCompany, companyProfilePic } = useAppSelector((store) => store.company);
  const loading = useAppSelector((store) => store.common.loading);
  const { percent, visible } = useAppSelector((store) => store.progress);
  const { createPostMedia, isMediaUploading } = useAppSelector((store) => store.socialMedia);

  const dispatch = useAppDispatch();
  const { handleUploadMedia } = useUploadMedia();

  // states
  const [linkPreviews, setLinkPreviews] = useState<Record<string, any>>({});
  const [fetchingMetadata, setFetchingMetadata] = useState<string | null>(null);

  // constant or derived states
  const userType = StorageUtils.get('userType') as 'User' | 'Company';
  const companyId = StorageUtils.get('companyId') as string;
  const maxCharCount = 75;

  const isAnnouncement = Boolean(postType === 'announcement');
  const postCreatorName: string = companyProfile ? myCompany?.name : profile?.name;
  const postCreatorProfilePic: string = companyProfile ? companyProfilePic : profile.profilePic;

  const isNoError = poll.question !== '' && poll.options.every((item) => item !== '')

  const openImageFileManager = async () => {
    if (uploadImageRef.current) {
      uploadImageRef.current.click();
    }
  };

  const openVideoFileManager = async () => {
    if (uploadVideoRef.current) {
      uploadVideoRef.current.click();
    }
  };

  const removeMedia = (mediaString: string) => {
    const extension = mediaString.split('.').pop() || '';

    const payload: TDeleteMediaActionRequest = {
      email: userType === 'User' ? profile?.email : myCompany?.email,
      id: mediaString,
      postId: editPostId,
      extension,
      userType,
      mediaType: 'image'
    };
    dispatch(deleteMedia(payload));
  };

  const newPostId = useMemo(() => {
    return uuidv4();
  }, [isModalOpen]);

  const onMediaLoad = (
    data: string | ArrayBuffer | null,
    fileType: string,
    mediaType: TMediaType
  ) => {
    const payload: TGetPostPresignedUrlActionRequest = {
      email: userType === 'User' ? profile?.email : myCompany?.email,
      id: uuidv4(),
      postId: newPostId,
      extension: fileType,
      userType,
      imageData: data,
      mediaType
    };
    const imageExtensions = ['jpg', 'jpeg', 'png', 'bmp', 'webp'];
    const videoExtensions = ['mp4', 'mov', 'avi', 'mkv'];

    let newMediaType: TMediaType;
    if (imageExtensions.includes(payload.extension.toLowerCase())) {
      newMediaType = 'image';
    } else if (videoExtensions.includes(payload.extension.toLowerCase())) {
      newMediaType = 'video';
    } else {
      newMediaType = 'all';
    }
    dispatch(getPostPresignedUrl({ ...payload, mediaType: newMediaType }));
  };

  const handleSubmit = (
    { text, title, customButton, redirectUrl }: TCreateEditPostModalFormValues,
    { setFieldError }: FormikHelpers<TCreateEditPostModalFormValues>
  ) => {
    setErrorTrig(false);
    if (isPoll && !isNoError) {
      setErrorTrig(true)
      return
    }
    console.log(!text?.trim(), 'textDatadfdf');

    if (!text?.trim() && !isAnnouncement && !isPoll && createPostMedia?.length === 0) {
      setFieldError('text', 'Please enter description');
      return undefined;
    }
    if (editMode) {
      const { options, ...rest } = poll
      const payload: TEditPostRequest & TInitialPoll = {
        userId: companyProfile ? companyId : profile.id,
        userType: companyProfile ? 'Company' : userType,
        postId: editPostId,
        urls: createPostMedia,
        linkPreview: !!Object.values(linkPreviews)?.[0],
        isAnnouncement,
        newText: text || ' ',
        ...(isAnnouncement && {
          announcement: {
            title,
            ...(customButton && { customButton }),
            ...(redirectUrl && { redirectUrl })
          }
        }),
        ...(isPoll && { poll: { ...rest, ...((!pollData || pollData?.options?.length !== options?.length || !pollData?.options?.every(obj => poll?.options?.includes(obj.text))) && { options }) } }),
        ...(isPoll && { isPoll })
      };
      dispatch(editPost(payload)).then(() => {
        dispatch(topFeedItems())
        if (refetchPost) {
          refetchPost();
        }
      });
    } else {
      const payload: TCreatePostRequest = {
        userId: companyProfile ? companyId : profile.id,
        userType: companyProfile ? 'Company' : userType,
        postId: newPostId,
        isAnnouncement,
        linkPreview: !!Object.values(linkPreviews)?.[0],
        timestamp: Date.now(),
        text: text || ' ',
        ...(createPostMedia?.length > 0 && { urls: createPostMedia }),
        ...(isAnnouncement && {
          announcement: {
            title,
            ...(customButton && { customButton }),
            ...(redirectUrl && { redirectUrl })
          }
        }),
        ...(isPoll && { poll: { ...poll } }),
        ...(isPoll && { isPoll })
      };

      dispatch(createPost(payload)).then(() => {
        if (refetchPost) {
          refetchPost();
        }
      });
    }
  };

  const handleCloseModal = () => {
    if (isMediaUploading === 0) {
      dispatch(clearPostMedia());
      onModalClose();
    }
  };

  // link preview functions 
  // const fetchLinkMetadata = useCallback(
  //   debounce(async (url: string) => {
  //     setFetchingMetadata(url);
  //     try {
  //       const data = await dispatch(getMetaData({ url }));
  //       if (data?.status) {
  //         if (Object.values(linkPreviews)?.length === 0 || !Object.values(linkPreviews)?.some((item) => item?.url === data?.data?.url)) {
  //           setLinkPreviews((prev) => ({ ...prev, [url]: { ...data?.data, image: data?.data?.image ? { url: data?.data?.image } : null } }));
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Error fetching link metadata:', error);
  //     } finally {
  //       setFetchingMetadata(null);
  //     }
  //   }, 700),
  //   []
  // );
  const fetchLinkMetadata = useCallback(
    debounce(async (url: string) => {
      setFetchingMetadata(url);
      try {
        const response = await axios.get(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
        // const data = await dispatch(getMetaData({ url: encodeURIComponent(url) }));
        // console.log(data, 'data55');
        if (response?.data?.status === 'success' && response.data.data) {
          if (Object.values(linkPreviews)?.length === 0 || !Object.values(linkPreviews)?.some((item) => item?.url === response.data.data?.url)) {
            setLinkPreviews((prev) => ({ ...prev, [url]: response.data.data }));
          }
        }
      } catch (error) {
        console.error('Error fetching link metadata:', error);
      } finally {
        setFetchingMetadata(null);
      }
    }, 700),
    []
  );

  const handleTextChange = (text: string) => {
    // Enhanced regex for detecting URLs without protocol
    const urlRegex =
      /\b((http(s)?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?\b/g;

    const urls = text.match(urlRegex);
    if (urls || urls?.length > 0) {
      if (!Object.values(linkPreviews)?.[0]) {
        const normalizedUrl = urls?.[0].startsWith('http') ? urls?.[0] : `https://${urls?.[0]}`;
        // Check if the URL is complete before fetching metadata
        if (!linkPreviews[normalizedUrl] && normalizedUrl !== fetchingMetadata) {
          fetchLinkMetadata(normalizedUrl); // Fetch metadata for the normalized URL
        }
        // Filter out invalid or partially entered URLs
        setLinkPreviews((prev) => {
          const currentPreviews = Object.keys(prev);
          const validUrls = urls.map((url) => (url.startsWith('http') ? url : `https://${url}`));

          const mergedPreviews = validUrls.reduce((acc, url) => {
            acc[url] = prev[url] || null; // Keep existing metadata or set it to null (if not fetched yet)
            return acc;
          }, {} as Record<string, any>);

          return mergedPreviews;
        });
      }
    } else {
      setLinkPreviews({}); // Clear all previews if no valid URLs are present
    }
  };

  useEffect(() => {
    if (postText && linkPreview) {
      handleTextChange(postText)
    }
  }, [postText, linkPreview])

  function handleRemove(key: string) {
    setLinkPreviews((prev) => {
      const updatedPreviews = { ...prev };
      delete updatedPreviews[key];
      return updatedPreviews;
    });
  }

  function handleAddRemovePollOption(type: string, i: number = 0) {
    if (type === 'add') {
      setPoll({ ...poll, options: [...poll.options, ''] })
    } else {
      setPoll((prev: TInitialPoll) => ({ ...prev, options: prev.options?.filter((_: string, index: number) => index !== i) }))
    }
  }

  useEffect(() => {
    if (pollData) {
      setPoll({
        id: pollData.id,
        question: pollData.question,
        allowMultipleValues: false,
        options: pollData?.options?.map((item) => item?.text)
      })
    }
  }, [pollData])

  return (
    <Modal
      isModalOpen={isModalOpen}
      onModalClose={handleCloseModal}
      title={`${title}${visible ? ` - Uploading ${percent}%` : ''}`}
      className="create-edit-post-modal"
    >
      <div className="create-edit-post-modal__content__header">
        <ProfilePicture
          url={postCreatorProfilePic || ''}
          fallback={textFormatter({
            value: postCreatorName[0]
          })}
        />
        <Typography as="span" variant="body-1" fontWeight="semiBold">
          {textFormatter({ value: postCreatorName })}
        </Typography>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={
          isAnnouncement
            ? createEditAnnouncementModalValidationSchema
            : createEditPostModalValidationSchema
        }
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, setFieldValue, touched, values }) => {
          useEffect(() => {
            if (emoji) {
              setFieldValue('text', emoji)
              setEmoji && setEmoji('')
            }
          }, [emoji])
          return (
            <Form noValidate className="create-edit-post-modal__content__form">
              {companyProfile && (
                <>
                  <Typography as="p" variant="body-2" fontWeight="semiBold">
                    Select post type
                  </Typography>
                  <div className="create-edit-post-modal__content__post-type">
                    <div
                      className={classNames('create-edit-post-modal__content__post-type__field', {
                        'create-edit-post-modal__content__post-type__field--is-disabled':
                          shareProgramMode
                      })}
                    >
                      <input
                        id="post"
                        type="radio"
                        name="postType"
                        onChange={() => {
                          setFieldValue('text', '');
                          setPostType('post');
                        }}
                        checked={postType === 'post'}
                        disabled={editMode || shareProgramMode}
                      />
                      <span className="custom-radio"></span>
                      <Label htmlFor="post">Regular Post</Label>
                    </div>
                    <div className="create-edit-post-modal__content__post-type__field">
                      <input
                        id="announcement"
                        type="radio"
                        name="postType"
                        onChange={() => {
                          setFieldValue('text', '');
                          setPostType('announcement');
                        }}
                        checked={postType === 'announcement'}
                        disabled={editMode}
                      />
                      <span className="custom-radio"></span>
                      <Label htmlFor="announcement">Announcement</Label>
                    </div>
                  </div>
                </>
              )}
              {isAnnouncement && (
                <>
                  {/* Announcement title */}
                  <InputField
                    label="Enter Announcement Title"
                    id="title"
                    variant={componentSizeVariantsEnum.SMALL}
                    placeholder="Announcement title"
                    type="text"
                    required
                  />

                  <div className="create-edit-post-modal__content__form__button-fields">
                    {/* Custom button text */}
                    <InputField
                      label="Enter the custom button Text"
                      id="customButton"
                      variant={componentSizeVariantsEnum.SMALL}
                      placeholder="Ex. Apply here"
                      type="text"
                    />
                    {/* Redirect url */}
                    <InputField
                      label="Enter the redirect URL"
                      id="redirectUrl"
                      variant={componentSizeVariantsEnum.SMALL}
                      placeholder="www.example.com"
                      type="text"
                      disabled={shareProgramMode || !values?.customButton}
                    />
                  </div>
                </>
              )}

              <TextAreaField
                id="text"
                label=""
                placeholder="Share Something"
                style={{ width: '100%' }}
                onChange={(e) => {
                  setFieldValue('text', e.target.value);
                  handleTextChange(e.target.value);
                }}
                value={values.text}
                errorMessage={errors.text}
                isTouched={touched.text}
                required
                inputRef={postDescriptionRef}
              />
              {isPoll && !isAnnouncement && <div className='create-edit-post-modal__content__poll'>
                <Typography as="h5" variant="body-3" fontWeight="semiBold" style={{ marginBottom: '14px' }}>
                  Create Poll
                </Typography>

                {/* poll question */}
                {/* <TextAreaField
                  id=""
                  style={{ marginTop: '-4px' }}
                  label="Your Question"
                  placeholder="Your Question?"
                  onChange={(e) => {
                    setPoll({ ...poll, question: e.target.value })
                  }}
                  value={poll.question}
                  errorMessage={!poll.question && errorTrig ? 'This field is required' : ''}
                  isTouched={errorTrig}
                  required
                /> */}
                <InputField
                  id="question"
                  style={{ marginTop: '-4px' }}
                  label="Your Question"
                  placeholder="Your Question"
                  onChange={(e) => {
                    setPoll({ ...poll, question: e.target.value })
                  }}
                  value={poll.question}
                  errorMessage={!poll.question && errorTrig ? 'This field is required' : ''}
                  isTouched={errorTrig}
                  required
                />
                {/* poll options  */}

                {poll.options.map((option: string, i: number) => {
                  const letterCount = option.length

                  return (
                    <div key={i} className='create-edit-post-modal__content__poll__optionBox'>
                      <InputField
                        removable={i !== 0}
                        onRemove={() => handleAddRemovePollOption('remove', i)}
                        style={{ marginTop: '-4px' }}
                        maxLength={maxCharCount}
                        label={`Option ${i + 1}`}
                        id={`poll-option-${i + 1}`}
                        onChange={(e) => {
                          const pollOptions = JSON.parse(JSON.stringify(poll.options));
                          const value = e.target.value;

                          pollOptions[i] = value
                          setPoll({ ...poll, options: pollOptions })
                        }}
                        value={option}
                        variant={componentSizeVariantsEnum.SMALL}
                        placeholder={`Option ${i + 1}`}
                        type="text"
                        errorMessage={errorTrig && !option ? 'This field is required' : ''}
                        isTouched={errorTrig}
                        required
                      />
                      <span className="create-edit-post-modal__content__poll__optionBox__char-count">{letterCount} / {maxCharCount}</span>
                    </div>
                  )
                })}
                <Button
                  variant="secondary"
                  size="small"
                  style={{ width: 'auto', marginTop: '10px' }}
                  onClick={() => handleAddRemovePollOption('add')}
                  disabled={isMediaUploading > 0}
                >
                  <MdAdd /> Add Option
                </Button>
              </div>}
              <Row>
                {/* Display link previews */}
                {Object.entries(linkPreviews).map(([key, metadata], index: number) => {
                  if (!metadata) {
                    return <></>
                  }

                  return (
                    <Col key={index} md={12}>
                      <div className="create-edit-post-modal__content__form__link-preview">
                        <LinkCard
                          clickable={false}
                          removable={true}
                          link={key}
                          onRemove={() => handleRemove(key)}
                          metadata={{
                            title: metadata?.title,
                            description: metadata?.description,
                            image: metadata?.image,
                            url: metadata?.url
                          }}
                        />
                      </div>
                    </Col>
                  )
                })}
              </Row>
              {/* Show loader for fetching link metadata */}
              {fetchingMetadata && (
                <div className="link-preview__loading">
                  <Loader />
                </div>
              )}

              <input
                type="file"
                hidden
                data-testid="inputUploadImage"
                ref={uploadImageRef}
                accept=".bmp,.jpg,.jpeg,.png, .mp4, .mov, .avi, .mkv, .webp"
                className="select-post-image"
                multiple
                onChange={(e) => {
                  dispatch(startMediaLoader());
                  handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'all' });
                }}
              />
              {/* <input
                type="file"
                hidden
                data-testid="inputUploadVideo"
                ref={uploadVideoRef}
                accept=".mp4"
                className="select-post-video"
                multiple
                onChange={(e) => {
                  handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'video' });
                }}
              /> */}
              <div className="create-edit-post-modal__content__actions">
                <div
                  className="create-edit-post-modal__content__actions__action"
                  onClick={openImageFileManager}
                >
                  <PictureIcon />
                </div>
                {/* <div
                  className="create-edit-post-modal__content__actions__action"
                  onClick={openVideoFileManager}
                >
                  <VideoIcon />
                </div> */}
                <div
                  className="create-edit-post-modal__content__actions__action"
                  onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
                >
                  <EmojiIcon />
                </div>
                {!isAnnouncement && <div
                  className="create-edit-post-modal__content__actions__action"
                  onClick={() => setIsPole((prev: boolean) => !prev)}
                >
                  <PollIcon />
                </div>}
              </div>
              <div className="create-edit-post-modal__content__media">
                {createPostMedia.map(({ type, key }: TCreatePostMedia, index) => {
                  return (
                    <div key={index} className="create-edit-post-modal__content__media__item">
                      {type === 'image' ? (
                        <img src={key} alt="selected media" />
                      ) : (
                        <video
                          src={key}
                          width="130"
                          height="130"
                          controls
                          poster="post vide thumbnail"
                        />
                      )}
                      <div
                        className="create-edit-post-modal__content__media__item__delete"
                        onClick={() => removeMedia(key)}
                      >
                        <TrashIcon />
                      </div>
                    </div>
                  );
                })}

                {isMediaUploading > 0 && (
                  <div
                    className={classNames('create-edit-post-modal__content__media__item', {
                      'create-edit-post-modal__content__media__item--is-uploading': isMediaUploading
                    })}
                  >
                    <Loader />
                  </div>
                )}
              </div>
              <div className="create-edit-post-modal__content__buttons">
                {!isPoll && (
                  <>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={handleCloseModal}
                      disabled={isMediaUploading > 0}
                    >
                      Cancel
                    </Button>
                    <Button
                      loading={loading}
                      variant="primary"
                      size="small"
                      type="submit"
                      disabled={isMediaUploading > 0 || loading}
                    >
                      {editMode ? 'Save' : 'Post'}
                    </Button>
                  </>
                )}
                {isPoll && (
                  <>
                    <button
                      className='create-edit-post-modal__content__buttons__poll-button__cancel'
                      onClick={handleCloseModal}
                      disabled={isMediaUploading > 0}
                    >
                      Cancel
                    </button>
                    <button className={`create-edit-post-modal__content__buttons__poll-button__submit ${isNoError ? 'active' : ''}`}>
                      {loading ? <Loader /> : editMode ? 'Save' : 'Post'}
                    </button>
                  </>
                )}
              </div>
              <EmojiPicker
                open={isEmojiPickerOpen}
                onEmojiClick={(e) => {
                  const postDescriptionEle = postDescriptionRef?.current;
                  if (postDescriptionEle) {
                    const startPos = postDescriptionEle?.selectionStart;

                    const textBeforeCursor = values?.text.substring(0, startPos);
                    const textAfterCursor = values?.text.substring(startPos);

                    setFieldValue('text', textBeforeCursor + e.emoji + textAfterCursor);
                  } else {
                    setFieldValue('text', values.text + e.emoji);
                  }
                  setIsEmojiPickerOpen(false);
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateEditPostModal;
